import { type ReactElement } from 'react';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';

import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { SocialLoginButtons } from '@/components/buttons/social-login-buttons';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import type { AppConfig } from '@/types/config';

export interface EatalyGuestProfileDropdownPanelProps {
    onNavigate?: () => void;
}

export function EatalyGuestProfileDropdownPanel({ onNavigate }: EatalyGuestProfileDropdownPanelProps): ReactElement {
    const { t } = useTranslation('header', { keyPrefix: 'guestMenu' });
    const config = useConfig<AppConfig>();
    const { site } = useSite();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    const isSocialLoginEnabled =
        Boolean(config.features.socialLogin?.enabled) && config.features.socialLogin.providers.length > 0;

    const loginAction = site
        ? buildUrl({
              to: '/login',
              urlConfig: config.url,
              params: { siteId: siteRef, localeId: localeRef },
          })
        : '/login';

    const signInRegisterButton = (
        <Button hierarchy="primary" mode="light" size="small" className="shrink-0 rounded-full px-5" asChild>
            <Link
                to="/login"
                onClick={() => onNavigate?.()}
                data-analytics-event="nav_cta_click"
                data-analytics-payload={JSON.stringify({
                    click_text: t('signInRegister'),
                    click_location: 'navbar',
                })}>
                {t('signInRegister')}
            </Link>
        </Button>
    );

    if (!isSocialLoginEnabled) {
        return (
            <div className="w-fit px-4 py-4" data-testid="eataly-guest-profile-dropdown-panel">
                {signInRegisterButton}
            </div>
        );
    }

    return (
        <div className="flex w-full min-w-0 flex-col gap-4 px-6 py-4" data-testid="eataly-guest-profile-dropdown-panel">
            <div className="flex items-center justify-between gap-4">
                <span className="body-copy-m-bold shrink-0 text-[var(--neutral-10)]">{t('proceedWith')}</span>
                <SocialLoginButtons formAction={loginAction} variant="compact" />
            </div>
            <div className="h-px w-full bg-[var(--sand-90)]" aria-hidden />
            <div className="flex items-center justify-between gap-4">
                <span className="body-copy-m-bold shrink-0 text-[var(--neutral-10)]">{t('or')}</span>
                {signInRegisterButton}
            </div>
        </div>
    );
}
